import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Grid, TextField, Button, CircularProgress, Alert, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import SingleRep from '../CardComponents/SingleRep';
import DuplicateRepresentatives from '../DuplicateDetection/DuplicateRepresentatives';
import { decrypt } from '../../../Security';

const VV = { primary: '#3F2B96', secondary: '#7F5AF0', accent: '#00C896', bg: '#F7F8FC', border: 'rgba(63,43,150,0.10)', shadow: '0 8px 24px rgba(0,0,0,0.08)' };

const SearchField = ({ icon, ...props }) => (
  <TextField variant="outlined" fullWidth size="small"
    InputProps={{ 
      startAdornment: icon ? (
        <InputAdornment position="start">
          {React.cloneElement(icon, { sx: { color: VV.secondary, fontSize: 18 } })}
        </InputAdornment>
      ) : undefined
    }}
    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: VV.secondary }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: VV.secondary, borderWidth: 2 } } }}
    {...props}
  />
);

const RepresentativeSearch = ({ setRepId }) => {
  const [name, setName] = useState('');
  const [adminArea, setAdminArea] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [reps, setReps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  console.log('🔍 RepresentativeSearch component mounted');
  console.log('📊 Current state:', { name, email, position, adminArea, reps: reps?.length, loading, error });

  const searchRep = async () => {
    try {
      console.log('🔎 Starting representative search...');
      console.log('📝 Search params:', { name, email, position, adminArea });
      
      setLoading(true);
      setError(null);
      
      const url = process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/reps/';
      console.log('🌐 API URL:', url);
      
      const response = await axios.get(url, {
        params: { name, email, position, adminArea },
        withCredentials: true
      });
      
      console.log('✅ API Response received:', response.status);
      
      const data = decrypt(response.data);
      console.log('📦 Decrypted data:', { repCount: data.reps?.length });
      
      setReps(data.reps);
      console.log('✅ Representatives set successfully');
    } catch (error) {
      console.error('❌ Error searching representatives:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError('Failed to search representatives. Please try again.');
    } finally {
      setLoading(false);
      console.log('🏁 Search complete');
    }
  };

  const resetSearch = () => {
    setName('');
    setAdminArea('');
    setEmail('');
    setPosition('');
    setReps(null);
    setError(null);
  };

  return (
    <Box sx={{ bgcolor: VV.bg, minHeight: '85vh', pb: 4 }}>
      <Box sx={{ background: `linear-gradient(135deg, ${VV.primary} 0%, ${VV.secondary} 100%)`, pt: 4, pb: 8, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <SearchIcon sx={{ color: VV.accent }} />
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 2 }}>Representative Search</Typography>
          </Box>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>Find Representatives</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5, fontSize: '0.9rem' }}>Search by name, email, position, or administrative area</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, mt: -5 }}>
        <Card sx={{ borderRadius: '18px', boxShadow: '0 20px 60px rgba(63,43,150,0.18)', mb: 3 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <SearchField icon={<PersonIcon />} label="Name" value={name} onChange={e => setName(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchField icon={<EmailIcon />} label="Email" value={email} onChange={e => setEmail(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchField icon={<BusinessIcon />} label="Administrative Area" value={adminArea} onChange={e => setAdminArea(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchField label="Position" value={position} onChange={e => setPosition(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button startIcon={<RefreshIcon />} onClick={resetSearch} variant="outlined"
                    sx={{ borderRadius: '10px', borderColor: VV.border, color: '#666', '&:hover': { borderColor: '#aaa' } }}>Reset</Button>
                  <Button startIcon={<SearchIcon />} onClick={searchRep} variant="contained" disabled={loading}
                    sx={{ borderRadius: '10px', px: 4, background: `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})`, fontWeight: 700, boxShadow: '0 4px 16px rgba(63,43,150,0.35)' }}>
                    {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Search'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ mb: 3 }}>
          <DuplicateRepresentatives />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

        {reps && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: VV.primary }}>Search Results</Typography>
              <Chip label={`${reps.length} found`} sx={{ bgcolor: `${VV.accent}22`, color: VV.accent, fontWeight: 700, borderRadius: '8px' }} />
            </Box>
            <Grid container spacing={3}>
              {reps.map((rep) => (
                <Grid item xs={12} md={4} key={rep._id}>
                  <SingleRep rep={rep} setRepId={setRepId} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default RepresentativeSearch;
